import React, { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { DefaultCalibrationLevelDataPoint } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionChartType';
import { getCalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { CalibrationLevelPill } from 'apps/sigma2-ui/src/components/utils/CalibrationLevelPill';
import { Percentage } from 'apps/sigma2-ui/src/components/Percentage';
import { AxisProps } from '@nivo/axes';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { getCalibrationLevelUIDetails } from 'libs/end-to-end/src/ui';
import { adjustCalibrationScoreForGraph } from 'apps/sigma2-ui/src/lib/graph-helpers';

export const getBarChartAxisLeft = ({
  language,
  calibrationFraming,
}: {
  language: Phrases;
  calibrationFraming: CalibrationFraming;
}): AxisProps => {
  return {
    tickPadding: 10,
    tickSize: 0,
    format: (calibrationScore) => {
      const value = adjustCalibrationScoreForGraph(
        calibrationScore,
        calibrationFraming,
      );

      // skip if value is not a whole number
      if (value % 1 !== 0) return '';

      const calibrationLevel = getCalibrationLevel(value);

      const uiDetails =
        getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel];
      return language[uiDetails.descriptionMessageId]();
    },
  };
};

export const CalibrationLevelBarChart: React.FC<{
  dataPoints: DefaultCalibrationLevelDataPoint[];
  calibrationFraming: CalibrationFraming;
}> = ({ dataPoints, calibrationFraming }) => {
  const language = useLanguage();

  const barChartKeys: string[] = dataPoints.map((result) => result.id) || [];
  const barChartColors: string[] = useMemo(() => {
    return (
      dataPoints.map((result) => {
        const calibrationLevel = getCalibrationLevel(result.calibrationScore);
        const uiDetails =
          getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel];
        return uiDetails.hexColor;
      }) || []
    );
  }, [dataPoints, calibrationFraming]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxHeight: '100%',
        aspectRatio: '1/1',
      }}
    >
      <ResponsiveBar
        data={dataPoints.map((datum) => {
          return {
            ...datum,
            [datum.id]: adjustCalibrationScoreForGraph(
              datum.calibrationScore,
              calibrationFraming,
            ),
          };
        })}
        valueScale={{ type: 'linear' }}
        margin={{ top: 50, right: 125, bottom: 100, left: 125 }}
        minValue={0}
        maxValue={6}
        borderRadius={3}
        colors={barChartColors}
        colorBy="id"
        label={() => ``}
        padding={0.25}
        innerPadding={3}
        keys={barChartKeys}
        indexBy={'id'}
        enableGridX={true}
        axisLeft={getBarChartAxisLeft({ language, calibrationFraming })}
        axisBottom={{
          tickPadding: 10,
          tickSize: 0,
          tickRotation: 30,
        }}
        tooltip={(d) => {
          const index = d.id;
          const levelData = dataPoints.find((x) => x.id === index);

          if (!levelData) {
            return null;
          }

          const calibrationLevel = getCalibrationLevel(
            levelData.calibrationScore,
          );

          const formatMoneyRange = (value: number) => {
            const min = Math.floor(value / 1000);
            const max = min + 1;
            return `$${min}k to $${max}k`;
          };

          const formatMoney = (value: number) => {
            return value.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            });
          };

          return (
            <div
              className="filter flex flex-col gap-1 shadow-8px bg-white text-left p-4 font-sans bg-opacity-70 backdrop-blur-sm rounded-md"
              data-qa={`tooltip${index}`}
            >
              <p
                className="text-xl font-bold font-sans"
                data-qa={`tooltip${index}Title`}
              >
                {index}
              </p>
              <CalibrationLevelPill
                calibrationLevel={calibrationLevel}
                data-qa={`tooltip${index}Pill`}
                calibrationFraming={calibrationFraming}
              />
              <div>
                <p>{language.payCorrectionTooltipPayGap()}</p>
                <p data-qa={`tooltip${index}PayGap`}>
                  <Percentage percentage={levelData.disparity} />
                </p>
              </div>
              <div>
                <p>{language.payCorrectionTooltipNeedsAttention()}</p>
                <p data-qa={`tooltip${index}NeedsAttention`}>
                  {levelData.needsAttention} employees
                </p>
              </div>
              <div>
                <p>{language.payCorrectionTooltipOnAverageUnderpaid()}</p>
                <p data-qa={`tooltip${index}OnAverageUnderpaid`}>
                  {formatMoneyRange(levelData.avgUnderpaid)}
                </p>
              </div>
              <div>
                <p>{language.payCorrectionTooltipBudgetNeeded()}</p>
                <p data-qa={`tooltip${index}BudgetNeeded`}>
                  {formatMoney(levelData.budgetNeeded)}
                </p>
              </div>
            </div>
          );
        }}
        theme={{
          text: {
            fontFamily: KARLA_FONT_FAMILY,
            fontSize: 14,
          },
          axis: {
            domain: {
              line: {
                stroke: '#b8babc',
                strokeWidth: 2,
              },
            },
            ticks: {
              line: {
                strokeWidth: 0,
              },
            },
            legend: {
              text: {
                stroke: '#b8babc',
                fontSize: 16,
                strokeWidth: 0.2,
              },
            },
          },
        }}
      />
    </div>
  );
};
